import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, articles } from '@/mocks/movies';
import { categoryNames } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);
  const movie = movies.find((m) => m.id === movieId);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-50 mb-2">影片未找到</h1>
              <p className="text-foreground-400 mb-4">抱歉，该影片不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const article = articles[movie.id];
  const relatedMovies = article?.relatedMovies
    ? movies.filter((m) => article.relatedMovies.includes(m.id))
    : [];

  const categoryName = categoryNames[movie.category] || movie.category;

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Breadcrumb */}
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${movie.category}` },
                  { label: movie.title },
                ]}
              />
            </div>
          </div>

          {/* Hero */}
          <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
            <img
              src={movie.heroUrl}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-transparent to-background-50"></div>
          </section>

          {/* Content */}
          <section className="px-4 md:px-6 lg:px-8 py-8 -mt-40 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Poster */}
                <div className="w-48 md:w-56 flex-shrink-0 mx-auto md:mx-0">
                  <div className="rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={movie.posterUrl}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - 高清电影免费观看`}
                      className="w-full aspect-[2/3] object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-foreground-400 text-sm mb-4">{movie.titleEn}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500/20 text-accent-400 text-sm font-bold">
                      <i className="ri-star-fill"></i> {movie.rating} 分
                    </span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-300 text-sm">
                      {movie.year}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-300 text-sm">
                      {movie.duration}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-300 text-sm">
                      {movie.region}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 rounded-md bg-secondary-500/10 text-secondary-400 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                    <p className="text-sm text-foreground-300">
                      <span className="text-foreground-500">导演：</span>{movie.director}
                    </p>
                    <p className="text-sm text-foreground-300">
                      <span className="text-foreground-500">主演：</span>{movie.actors.join(' / ')}
                    </p>
                    <p className="text-sm text-foreground-300">
                      <span className="text-foreground-500">上映日期：</span>{movie.releaseDate}
                    </p>
                    <p className="text-sm text-foreground-300">
                      <span className="text-foreground-500">类型：</span>{categoryName}
                    </p>
                  </div>

                  <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap mb-6">
                    <i className="ri-play-fill text-lg"></i> 立即播放
                  </button>
                </div>
              </div>

              {/* Description */}
              {article && (
                <div className="mt-10">
                  <h2 className="text-lg font-bold text-foreground-50 mb-4 font-heading">剧情简介</h2>
                  <div
                    className="text-sm text-foreground-300 leading-relaxed space-y-3"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <h2 className="text-lg font-bold text-foreground-50 mt-8 mb-4 font-heading">影片亮点</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {article.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-100">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500/20 flex-shrink-0">
                          <i className="ri-check-line text-accent-500"></i>
                        </div>
                        <span className="text-sm text-foreground-200">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="px-4 md:px-6 lg:px-8 py-10">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-50 mb-6 font-heading">
                  <i className="ri-links-line text-primary-500 mr-2"></i>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQ */}
          {article?.faqs && article.faqs.length > 0 && (
            <section className="px-4 md:px-6 lg:px-8 py-10 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-50 mb-6 font-heading text-center">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, i) => (
                    <details key={i} className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden">
                      <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-100 list-none">
                        <span>{faq.question}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-4 pb-4 text-sm text-foreground-400 leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${movie.title} - 高清电影在线观看`,
              description: movie.description,
              datePublished: movie.releaseDate,
              author: { '@type': 'Person', name: movie.director },
              publisher: { '@type': 'Organization', name: '6080电影网' },
            }),
          }}
        />
        {article?.faqs && article.faqs.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: article.faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
                })),
              }),
            }}
          />
        )}

        <Footer />
      </div>
    </>
  );
}